/**
 * What table a seed or a day gets: its size, and how many object balls (GEN-002).
 *
 * `generateLayout` places balls for a size and a count it is GIVEN; this decides
 * them. It lives in `sim` for the reason layout does: the browser and the offline
 * generator must agree on it exactly.
 *
 * Deterministic and transcendental-free like everything else here (ARCHITECTURE.md
 * §5.5).
 */
import { DEFAULT_DIFFICULTY_CURVE, dayOfWeek } from './layout.ts';
import { createRng, type Rng } from './random.ts';
import type { TableSizeFt } from './table.ts';

export interface Deal {
  readonly seed: string;
  readonly sizeFt: TableSizeFt;
  readonly ballCount: number;
}

const SIZES: readonly TableSizeFt[] = [7, 8, 9];

/**
 * The most object balls each size takes. A 7ft table has 61% of a 9ft table's area,
 * so ten balls on it is as crowded as sixteen on a 9ft — and crowding turns a puzzle
 * into luck: clusters, blocked pockets, forced safeties. Owner-tunable, like the
 * weekday curve (PRD D-014).
 */
export const MAX_BALLS_FOR_SIZE: Readonly<Record<TableSizeFt, number>> = {
  7: 8,
  8: 9,
  9: 10,
};

/** An object-ball count from a weekday's range, capped for the table. */
function ballCountFor(weekday: number, sizeFt: TableSizeFt, rng: Rng): number {
  const [lo, hi] = DEFAULT_DIFFICULTY_CURVE[weekday] ?? [5, 8];
  const cap = MAX_BALLS_FOR_SIZE[sizeFt];
  // Every range's top is at most 8, and every cap at least 8, so since D-046 the cap
  // never binds on a daily table; it still guards a curve someone retunes later.
  const top = hi < cap ? hi : cap;
  return rng.int(lo < top ? lo : top, top);
}

/**
 * A whole table from a seed — the lab's "New table". Size is uniform; the ball count
 * comes from a weekday picked by the seed too, so dealing repeatedly walks the whole
 * spread of tables the daily game will produce.
 */
export function dealForSeed(seed: string): Deal {
  const rng = createRng(`deal:${seed}`);
  const sizeFt = SIZES[rng.int(0, SIZES.length - 1)] ?? 9;
  const weekday = rng.int(0, 6);
  return { seed, sizeFt, ballCount: ballCountFor(weekday, sizeFt, rng) };
}

/**
 * The table size for day `dayIndex` (days since launch).
 *
 * A shuffled bag: each block of three days is 7, 8 and 9ft in a seeded order. So the
 * same size can never come up more than twice running, no size is ever away for more
 * than four days, and each gets exactly a third of the days — where an independent
 * pick per day would give the same size four or five days in a row several times a
 * year.
 *
 * `salt` is derived offline from the secret seed key (levelgen `seeds.ts`). Without it,
 * anyone holding this code could work out which size every future day gets (N7).
 */
export function tableSizeForDay(dayIndex: number, salt: string): TableSizeFt {
  if (!Number.isInteger(dayIndex) || dayIndex < 0) {
    throw new Error(
      `tableSizeForDay: dayIndex must be a whole number ≥ 0, got ${String(dayIndex)}`,
    );
  }
  if (salt === '') throw new Error('tableSizeForDay: salt must come from the seed key');
  const block = Math.floor(dayIndex / 3);
  const rng = createRng(`size-block:${salt}:${String(block)}`);
  // Fisher–Yates on a copy.
  const bag = [...SIZES];
  for (let i = bag.length - 1; i > 0; i--) {
    const j = rng.int(0, i);
    const held = bag[i] ?? 9;
    bag[i] = bag[j] ?? 9;
    bag[j] = held;
  }
  return bag[dayIndex % 3] ?? 9;
}

/**
 * The daily table: size from the bag, ball count from the weekday curve, capped for
 * the size. `dateKey` is the day's `YYYY-MM-DD`, which picks the weekday; `salt` is the
 * secret-derived value that keeps future days unguessable (see `tableSizeForDay`).
 * The returned `seed` is not a layout seed: levelgen derives those separately.
 */
export function dealForDay(dayIndex: number, dateKey: string, salt: string): Deal {
  const sizeFt = tableSizeForDay(dayIndex, salt);
  const rng = createRng(`balls:${salt}:${dateKey}`);
  return { seed: dateKey, sizeFt, ballCount: ballCountFor(dayOfWeek(dateKey), sizeFt, rng) };
}
